/**
 * DerivedPriceChart - Tier 1 Compliance Line Chart
 * Displays ONLY daily close prices - NO OHLCV data
 * This replaces the candlestick chart for data compliance
 */
import { useEffect, useRef, useCallback, useState } from 'react';
import { createPortal } from 'react-dom';
import { createChart, LineSeries, CrosshairMode } from 'lightweight-charts';
import { 
  RotateCcw,
  Loader2,
  RefreshCw,
  AlertCircle,
  Maximize2,
  Minimize2,
  X
} from 'lucide-react';
import {
  Tooltip as UITooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from './ui/tooltip';

// Format price
const formatPrice = (price) => {
  if (price == null || isNaN(price)) return '-';
  return `$${Number(price).toFixed(2)}`;
};

// Format date for tooltip
const formatDate = (time) => {
  if (!time) return '';
  if (typeof time === 'object' && time.year) {
    const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
    return `${months[time.month - 1]} ${time.day}, ${time.year}`;
  }
  return '';
};

// Simple Tooltip for line chart
const LineTooltip = ({ 
  date, 
  value, 
  position, 
  isVisible, 
  symbol,
  containerBounds
}) => {
  const [adjustedPosition, setAdjustedPosition] = useState({ x: 0, y: 0 });
  
  useEffect(() => {
    if (!position || !containerBounds) return;
    
    let x = position.x + 15;
    let y = position.y - 30;
    
    if (x + 150 > containerBounds.width - 10) {
      x = position.x - 165;
    }
    if (x < 10) x = 10;
    if (y < 10) y = 10;
    if (y + 60 > containerBounds.height - 10) {
      y = containerBounds.height - 70;
    }
    
    setAdjustedPosition({ x, y });
  }, [position, containerBounds]);
  
  if (!isVisible || value == null) return null;
  
  return (
    <div
      className="absolute z-50 pointer-events-none"
      style={{
        left: adjustedPosition.x,
        top: adjustedPosition.y,
      }}
    >
      <div 
        className="rounded-lg shadow-xl backdrop-blur-sm px-3 py-2"
        style={{
          background: 'rgba(24, 24, 27, 0.95)',
          border: '1px solid rgba(255, 255, 255, 0.1)',
        }}
      >
        <div className="flex items-center gap-2 mb-1">
          <span className="text-[11px] font-semibold" style={{ color: '#fafafa' }}>
            {symbol}
          </span>
          <span className="text-[9px]" style={{ color: '#71717a' }}>•</span>
          <span className="text-[9px]" style={{ color: '#a1a1aa' }}>
            {formatDate(date)}
          </span>
        </div>
        <div className="flex items-center gap-2">
          <span className="text-[9px]" style={{ color: '#71717a' }}>Close</span>
          <span className="text-sm font-mono font-semibold" style={{ color: '#22c55e' }}>
            {formatPrice(value)}
          </span>
        </div>
      </div>
    </div>
  );
};

// Loading Overlay
const LoadingOverlay = () => (
  <div className="absolute inset-0 flex items-center justify-center bg-black/40 rounded-lg z-10">
    <div className="flex items-center gap-2 px-3 py-2 rounded-md" style={{ backgroundColor: '#18181b' }}>
      <Loader2 className="h-4 w-4 animate-spin" style={{ color: '#6366f1' }} />
      <span className="text-xs" style={{ color: '#a1a1aa' }}>Loading chart...</span>
    </div>
  </div>
);

// Error Display
const ErrorDisplay = ({ symbol, onRetry }) => (
  <div className="flex flex-col items-center justify-center h-64 p-4">
    <AlertCircle className="h-8 w-8 mb-3" style={{ color: '#ef4444' }} />
    <p className="text-sm text-center mb-1" style={{ color: '#fafafa' }}>
      Could not load chart data
    </p>
    <p className="text-xs text-center mb-4" style={{ color: '#71717a' }}>
      {symbol} - Daily Close
    </p>
    <button
      type="button"
      onClick={onRetry}
      className="flex items-center gap-2 px-3 py-1.5 rounded-md text-xs font-medium transition-colors hover:bg-white/10"
      style={{ backgroundColor: 'rgba(99, 102, 241, 0.15)', color: '#a5b4fc' }}
    >
      <RefreshCw className="h-3.5 w-3.5" />
      Try Again
    </button>
  </div>
);

/**
 * DerivedPriceChart Component
 * Displays daily close prices as a line chart
 * 
 * Props:
 * - data: Array of {date: string, value: number}
 * - symbol: Stock ticker
 * - loading: Loading state
 * - error: Error message
 * - onRetry: Retry callback
 */
const DerivedPriceChart = ({ 
  data, 
  symbol = '',
  loading = false,
  error = null,
  onRetry
}) => {
  const containerRef = useRef(null);
  const chartRef = useRef(null);
  const lineSeriesRef = useRef(null);
  
  const [isExpanded, setIsExpanded] = useState(false);
  const [windowHeight, setWindowHeight] = useState(typeof window !== 'undefined' ? window.innerHeight : 800);
  
  // Tooltip state
  const [tooltipData, setTooltipData] = useState({ date: null, value: null });
  const [tooltipPosition, setTooltipPosition] = useState({ x: 0, y: 0 });
  const [isTooltipVisible, setIsTooltipVisible] = useState(false);
  const [containerBounds, setContainerBounds] = useState({ width: 0, height: 0 });

  useEffect(() => {
    const updateWindowHeight = () => setWindowHeight(window.innerHeight);
    updateWindowHeight();
    window.addEventListener('resize', updateWindowHeight);
    return () => window.removeEventListener('resize', updateWindowHeight);
  }, []);

  // Initialize chart
  useEffect(() => {
    if (!containerRef.current || chartRef.current) return;

    // Force locale to prevent "Invalid language tag: en-US@posix" error
    const safeLocale = 'en-US';
    
    const chart = createChart(containerRef.current, {
      width: containerRef.current.clientWidth,
      height: 380,
      layout: {
        background: { type: 'solid', color: 'transparent' },
        textColor: '#71717a',
        fontFamily: 'Inter, system-ui, sans-serif',
        attributionLogo: false,
      },
      grid: {
        vertLines: { color: 'rgba(255,255,255,0.03)' },
        horzLines: { color: 'rgba(255,255,255,0.03)' },
      },
      crosshair: {
        mode: CrosshairMode.Normal,
        vertLine: {
          color: 'rgba(99, 102, 241, 0.4)',
          width: 1,
          style: 2,
          labelBackgroundColor: '#18181b',
        },
        horzLine: {
          color: 'rgba(99, 102, 241, 0.4)',
          width: 1,
          style: 2,
          labelBackgroundColor: '#18181b',
        },
      },
      localization: {
        locale: safeLocale,
        dateFormat: 'yyyy-MM-dd',
        priceFormatter: (price) => {
          if (price == null || isNaN(price)) return '';
          return price.toFixed(2);
        },
      },
      timeScale: {
        visible: true,
        borderColor: '#3f3f46',
        rightOffset: 10,
        barSpacing: 6,
        minBarSpacing: 3,
        tickMarkFormatter: (time) => {
          if (typeof time === 'object' && time.year) {
            const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
            return `${months[time.month - 1]} ${time.day}`;
          }
          return '';
        },
      },
      rightPriceScale: {
        borderColor: '#3f3f46',
        scaleMargins: { top: 0.1, bottom: 0.1 },
      },
      handleScroll: {
        mouseWheel: true,
        pressedMouseMove: true,
      },
      handleScale: {
        mouseWheel: true,
        pinch: true,
      },
    });

    // Create line series (ONLY close prices)
    const lineSeries = chart.addSeries(LineSeries, {
      color: '#22c55e',
      lineWidth: 2,
      crosshairMarkerVisible: true,
      crosshairMarkerRadius: 4,
      crosshairMarkerBorderColor: '#22c55e',
      crosshairMarkerBackgroundColor: '#09090b',
      priceFormat: {
        type: 'price',
        precision: 2,
        minMove: 0.01,
      },
    });

    // Subscribe to crosshair move
    chart.subscribeCrosshairMove((param) => {
      if (containerRef.current) {
        const rect = containerRef.current.getBoundingClientRect();
        setContainerBounds({ width: rect.width, height: rect.height });
      }
      
      if (param.time && param.seriesData.size > 0 && param.point) {
        const lineData = param.seriesData.get(lineSeries);
        if (lineData) {
          setTooltipData({
            date: param.time,
            value: lineData.value
          });
          setTooltipPosition({ x: param.point.x, y: param.point.y });
          setIsTooltipVisible(true);
        }
      } else {
        setIsTooltipVisible(false);
      }
    });

    chartRef.current = chart;
    lineSeriesRef.current = lineSeries;

    // Resize observer
    const resizeObserver = new ResizeObserver((entries) => {
      if (entries[0] && chartRef.current) {
        const { width, height } = entries[0].contentRect;
        if (width > 0 && height > 0) {
          chartRef.current.applyOptions({ width, height });
        }
      }
    });
    
    if (containerRef.current) {
      resizeObserver.observe(containerRef.current);
    }

    return () => {
      resizeObserver.disconnect();
      if (chartRef.current) {
        chartRef.current.remove();
        chartRef.current = null;
        lineSeriesRef.current = null;
      }
    };
  }, []);

  // Handle expanded state
  useEffect(() => {
    if (!chartRef.current || !containerRef.current) return;
    
    const rafId = requestAnimationFrame(() => {
      setTimeout(() => {
        if (chartRef.current && containerRef.current) {
          const rect = containerRef.current.getBoundingClientRect();
          const newHeight = isExpanded ? Math.floor(windowHeight * 0.7) : 380;
          const newWidth = rect.width > 0 ? rect.width : containerRef.current.clientWidth;
          
          if (newWidth > 0 && newHeight > 0) {
            chartRef.current.resize(newWidth, newHeight);
            chartRef.current.timeScale().fitContent();
          }
        }
      }, 150);
    });
    
    return () => cancelAnimationFrame(rafId);
  }, [isExpanded, windowHeight]);

  // ESC key handler
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === 'Escape' && isExpanded) {
        setIsExpanded(false);
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [isExpanded]);

  // Body scroll lock
  useEffect(() => {
    if (isExpanded) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => { document.body.style.overflow = ''; };
  }, [isExpanded]);

  // Update data
  useEffect(() => {
    if (!lineSeriesRef.current || !data || data.length === 0) return;

    // Convert data to chart format
    const chartData = data
      .filter(d => d.date && d.value != null)
      .map(d => {
        const dateObj = new Date(d.date);
        return {
          time: {
            year: dateObj.getUTCFullYear(),
            month: dateObj.getUTCMonth() + 1,
            day: dateObj.getUTCDate()
          },
          value: d.value
        };
      })
      .sort((a, b) => {
        const dateA = new Date(a.time.year, a.time.month - 1, a.time.day);
        const dateB = new Date(b.time.year, b.time.month - 1, b.time.day);
        return dateA - dateB;
      });

    if (chartData.length > 0) {
      lineSeriesRef.current.setData(chartData);
      chartRef.current?.timeScale().fitContent();
    }
  }, [data]);

  // Handlers
  const handleResetZoom = useCallback(() => {
    if (chartRef.current) {
      chartRef.current.timeScale().fitContent();
    }
  }, []);

  const handleToggleExpand = useCallback(() => {
    setIsExpanded(prev => !prev);
  }, []);

  // Error state
  if (error) {
    return <ErrorDisplay symbol={symbol} onRetry={onRetry} />;
  }

  // Empty state
  if (!data || data.length === 0) {
    if (loading) {
      return (
        <div className="relative h-[380px]">
          <LoadingOverlay />
        </div>
      );
    }
    return (
      <div className="flex items-center justify-center h-[380px]" style={{ color: '#A1A1AA' }}>
        No data available
      </div>
    );
  }

  // Chart content
  const chartContent = (
    <div className="relative">
      {loading && <LoadingOverlay />}
      
      {/* Controls */}
      <div className="flex items-center justify-between gap-3 mb-4">
        <div className="flex items-center gap-2">
          <span className="text-[9px] uppercase tracking-wider" style={{ color: '#52525b' }}>
            Daily Close Price
          </span>
          <span className="text-[9px] px-1.5 py-0.5 rounded" style={{ backgroundColor: 'rgba(34, 197, 94, 0.1)', color: '#4ade80' }}>
            Derived Data
          </span>
        </div>

        <div className="flex items-center gap-2">
          <TooltipProvider>
            <UITooltip>
              <TooltipTrigger asChild>
                <button
                  type="button"
                  onClick={handleResetZoom}
                  className="p-1.5 rounded hover:bg-white/5 transition-colors"
                  data-testid="reset-zoom"
                >
                  <RotateCcw className="h-3.5 w-3.5" style={{ color: '#71717a' }} />
                </button>
              </TooltipTrigger>
              <TooltipContent side="bottom">
                <p className="text-xs">Reset zoom</p>
              </TooltipContent>
            </UITooltip>
          </TooltipProvider>

          <TooltipProvider>
            <UITooltip>
              <TooltipTrigger asChild>
                <button
                  type="button"
                  onClick={handleToggleExpand}
                  className="p-1.5 rounded hover:bg-white/5 transition-colors"
                  data-testid="expand-chart"
                >
                  {isExpanded ? (
                    <Minimize2 className="h-3.5 w-3.5" style={{ color: '#71717a' }} />
                  ) : (
                    <Maximize2 className="h-3.5 w-3.5" style={{ color: '#71717a' }} />
                  )}
                </button>
              </TooltipTrigger>
              <TooltipContent side="bottom">
                <p className="text-xs">{isExpanded ? 'Exit fullscreen' : 'Expand chart'}</p>
              </TooltipContent>
            </UITooltip>
          </TooltipProvider>
        </div>
      </div>

      {/* Chart container */}
      <div style={{ position: 'relative' }}>
        <div 
          ref={containerRef} 
          style={{ 
            width: '100%', 
            height: isExpanded ? `${Math.floor(windowHeight * 0.7)}px` : '380px',
          }}
          data-testid="derived-price-chart-container"
        />
        
        <LineTooltip
          date={tooltipData.date}
          value={tooltipData.value}
          position={tooltipPosition}
          isVisible={isTooltipVisible}
          symbol={symbol}
          containerBounds={containerBounds}
        />
      </div>

      {/* Compliance notice */}
      <p className="mt-3 text-[9px]" style={{ color: '#3f3f46' }}>
        Daily closing prices only. For research purposes.
      </p>
    </div>
  );

  // Render with expand support
  return (
    <>
      {isExpanded && createPortal(
        <div 
          className="fixed inset-0 z-40"
          style={{ backgroundColor: 'rgba(0,0,0,0.85)' }}
          onClick={handleToggleExpand}
        />,
        document.body
      )}
      
      <div 
        className={isExpanded ? 'fixed z-50 p-6 rounded-xl' : 'relative'}
        style={isExpanded ? { 
          top: '5vh',
          left: '2.5vw',
          width: '95vw',
          height: '90vh',
          background: 'linear-gradient(135deg, #0a0a0f 0%, #12121a 100%)',
          border: '1px solid rgba(255,255,255,0.1)',
        } : {}}
      >
        {isExpanded && (
          <button
            type="button"
            onClick={handleToggleExpand}
            className="absolute top-4 right-4 p-2 rounded-full hover:bg-white/10 transition-colors z-10"
          >
            <X className="h-5 w-5" style={{ color: '#a1a1aa' }} />
          </button>
        )}
        
        {isExpanded && (
          <div className="text-[10px] uppercase tracking-wider mb-4 font-medium" style={{ color: '#71717a' }}>
            {symbol} Daily Close • Press ESC to close
          </div>
        )}
        
        {chartContent}
      </div>
    </>
  );
};

export default DerivedPriceChart;
